import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Admins() {
  const { events } = useOutletContext();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState({});

  const load = async () => {
    const { data } = await api.get("/admins");
    setAdmins(data);
    return data;
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admins", form);
      await load();
      setForm({ email: "", name: "", password: "" });
      setCreating(false);
      toast.success("Admin created");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleAssign = async (admin, eventId, checked) => {
    const key = `${admin.id}:${eventId}`;
    setAssigning((s) => ({ ...s, [key]: true }));
    try {
      if (checked) {
        await api.post(`/events/${eventId}/admins`, { user_id: admin.id });
      } else {
        await api.delete(`/events/${eventId}/admins/${admin.id}`);
      }
      await load();
      toast.success(checked ? "Assigned" : "Unassigned");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setAssigning((s) => ({ ...s, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="admins-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admins</h1>
          <p className="mt-1 text-sm text-[#525252]">
            Create event admins and assign them to one or more events
          </p>
        </div>
        <Button
          onClick={() => setCreating((v) => !v)}
          className="bg-[#1A362D] hover:bg-[#12271f]"
          data-testid="create-admin-toggle"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Admin
        </Button>
      </div>

      {creating && (
        <form onSubmit={create} className="space-y-4 rounded-md border border-[#E5E5E5] bg-white p-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="new-admin-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-admin-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Initial password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="new-admin-password"
              />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="create-admin-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create admin"}
          </Button>
        </form>
      )}

      <div className="space-y-4">
        {admins.length === 0 ? (
          <div className="rounded-md border border-[#E5E5E5] bg-white p-8 text-center text-sm text-[#525252]">
            No event admins yet.
          </div>
        ) : (
          admins.map((admin) => {
            const assigned = new Set(admin.event_ids || []);
            return (
              <div key={admin.id} className="rounded-md border border-[#E5E5E5] bg-white p-5">
                <div className="mb-3">
                  <div className="font-medium">{admin.name || "Admin"}</div>
                  <div className="text-sm text-[#525252]">{admin.email}</div>
                </div>
                {(events || []).length === 0 ? (
                  <p className="text-sm text-[#525252]">Create an event first to assign access.</p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {events.map((ev) => {
                      const key = `${admin.id}:${ev.id}`;
                      const checked = assigned.has(ev.id);
                      return (
                        <label
                          key={ev.id}
                          className="flex cursor-pointer items-center gap-3 rounded-md border border-[#E5E5E5] px-3 py-2 text-sm"
                        >
                          <Checkbox
                            checked={checked}
                            disabled={!!assigning[key]}
                            onCheckedChange={(v) => toggleAssign(admin, ev.id, !!v)}
                          />
                          <span>{ev.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
